import React, { useEffect, useRef, useState } from 'react';

import { TActiveColumnStateSyncProps } from '@/shared/components/ui/data-table/components/column-state-sync/column-state-sync.types';
import {
  canApplyOrder,
  toStoredColumnState,
} from '@/shared/components/ui/data-table/components/column-state-sync/column-state-sync.utils';
import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import { useFilterScope } from '@/shared/features/filter-preferences/context/filter-scope-context';
import { useFilterPreferences } from '@/shared/features/filter-preferences/hooks/use-filter-preferences/use-filter-preferences';
import { useTableViewState } from '@/shared/features/filter-preferences/hooks/use-table-view-state/use-table-view-state';

// Width, order and sort live only in ag-grid's memory, so a remount loses them.
const ActiveColumnStateSync: React.FC<TActiveColumnStateSyncProps> = ({
  scopeKey,
}) => {
  const { gridApi } = useDataTable();
  const { readSnapshot, saveColumns, seedColumns } =
    useTableViewState(scopeKey);
  const { getStoredColumns, isReady } = useFilterPreferences();
  const [seeded, setSeeded] = useState(false);

  // On a cold load the grid is ready long before the saved layout is.
  const sources = useRef({ readSnapshot, getStoredColumns, seedColumns });
  sources.current = { readSnapshot, getStoredColumns, seedColumns };

  // The saved layout goes into the remembered one first, so everything below
  // reads a single source and later changes layer on top of it.
  useEffect(() => {
    if (!isReady) return;

    sources.current.seedColumns(
      sources.current.getStoredColumns(scopeKey) ?? [],
    );
    setSeeded(true);
  }, [scopeKey, isReady]);

  useEffect(() => {
    if (!gridApi || !seeded) return;

    const apply = () => {
      const stored =
        sources.current.readSnapshot()?.columns ??
        sources.current.getStoredColumns(scopeKey);
      if (!stored?.length) return;

      gridApi.applyColumnState({
        state: stored,
        applyOrder: canApplyOrder(stored, gridApi.getColumnState()),
      });
    };

    apply();
    // ag-grid resets column state whenever the definitions are re-applied, and
    // any colDef identity change triggers that -- a value getter resolving is enough.
    gridApi.addEventListener('newColumnsLoaded', apply);

    return () => {
      gridApi.removeEventListener('newColumnsLoaded', apply);
    };
  }, [gridApi, scopeKey, seeded]);

  // Capturing before the seed would persist ag-grid's own start-up sizing and
  // that snapshot, being the layer that wins, would bury the saved layout.
  useEffect(() => {
    if (!gridApi || !seeded) return;

    const capture = (event: { source?: string; finished?: boolean }) => {
      // 'api' is our own applyColumnState echoing back; unfinished is a live drag.
      if (event.source === 'api' || event.finished === false) return;

      saveColumns(toStoredColumnState(gridApi.getColumnState()));
    };

    gridApi.addEventListener('columnResized', capture);
    gridApi.addEventListener('columnMoved', capture);
    gridApi.addEventListener('sortChanged', capture);

    return () => {
      gridApi.removeEventListener('columnResized', capture);
      gridApi.removeEventListener('columnMoved', capture);
      gridApi.removeEventListener('sortChanged', capture);
    };
  }, [gridApi, saveColumns, seeded]);

  return null;
};

export const ColumnStateSync: React.FC = () => {
  const { scopeKey } = useFilterScope();

  if (!scopeKey) {
    return null;
  }

  return <ActiveColumnStateSync scopeKey={scopeKey} />;
};
